// ============================================================================
// SLA TIMER COMPONENT
// Visual countdown timer for Support Desk SLA tracking
// Date: October 25, 2025
// ============================================================================

'use client';

import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, AlertCircle, Timer, Zap } from 'lucide-react';

export interface SLATimerProps {
  deadline: string | null; // ISO timestamp
  breached: boolean;
  breachedAt?: string | null;
  label?: string; // 'First Response' or 'Resolution'
  variant?: 'compact' | 'full' | 'badge';
  isPaused?: boolean; // For "Waiting on Customer" status
  showIcon?: boolean;
  className?: string;
}

interface TimeRemaining {
  total: number; // milliseconds
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isOverdue: boolean;
}

function calculateTimeRemaining(deadline: string | null): TimeRemaining {
  if (!deadline) {
    return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0, isOverdue: false };
  }

  const now = new Date().getTime();
  const deadlineTime = new Date(deadline).getTime();
  const difference = deadlineTime - now;

  if (difference <= 0) {
    const absoluteDiff = Math.abs(difference);
    return {
      total: difference,
      days: Math.floor(absoluteDiff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((absoluteDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((absoluteDiff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((absoluteDiff % (1000 * 60)) / 1000),
      isOverdue: true
    };
  }

  return {
    total: difference,
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
    isOverdue: false
  };
}

function getColorClasses(timeRemaining: TimeRemaining, breached: boolean, isPaused: boolean): {
  bg: string;
  border: string;
  text: string;
  icon: string;
} {
  if (isPaused) {
    return {
      bg: 'bg-gray-100',
      border: 'border-gray-300',
      text: 'text-gray-700',
      icon: 'text-gray-500'
    };
  }

  if (breached || timeRemaining.isOverdue) {
    return {
      bg: 'bg-red-100',
      border: 'border-red-400',
      text: 'text-red-900',
      icon: 'text-red-600'
    };
  }

  const totalHours = timeRemaining.total / (1000 * 60 * 60);

  if (totalHours <= 1) {
    return {
      bg: 'bg-orange-100',
      border: 'border-orange-400',
      text: 'text-orange-900',
      icon: 'text-orange-600'
    };
  }

  if (totalHours <= 4) {
    return {
      bg: 'bg-yellow-100',
      border: 'border-yellow-400',
      text: 'text-yellow-900',
      icon: 'text-yellow-600'
    };
  }

  return {
    bg: 'bg-green-100',
    border: 'border-green-400',
    text: 'text-green-900',
    icon: 'text-green-600'
  };
}

function getStatusIcon(timeRemaining: TimeRemaining, breached: boolean, isPaused: boolean) {
  if (isPaused) return Timer;
  if (breached || timeRemaining.isOverdue) return AlertTriangle;
  
  const totalHours = timeRemaining.total / (1000 * 60 * 60);
  if (totalHours <= 1) return AlertCircle;
  if (totalHours <= 4) return Clock;
  return CheckCircle;
}

function formatTimeDisplay(timeRemaining: TimeRemaining, isPaused: boolean): string {
  if (isPaused) {
    return 'Paused';
  }

  if (timeRemaining.isOverdue) {
    if (timeRemaining.days > 0) {
      return `${timeRemaining.days}d ${timeRemaining.hours}h overdue`;
    }
    if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m overdue`;
    }
    return `${timeRemaining.minutes}m overdue`;
  }

  if (timeRemaining.days > 0) {
    return `${timeRemaining.days}d ${timeRemaining.hours}h`;
  }
  if (timeRemaining.hours > 0) {
    return `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
  }
  if (timeRemaining.minutes > 0) {
    return `${timeRemaining.minutes}m`;
  }
  return `${timeRemaining.seconds}s`;
}

export default function SLATimer({
  deadline,
  breached,
  breachedAt,
  label = 'SLA',
  variant = 'full',
  isPaused = false,
  showIcon = true,
  className = ''
}: SLATimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() => 
    calculateTimeRemaining(deadline)
  );

  useEffect(() => {
    if (!deadline || breached) {
      setTimeRemaining(calculateTimeRemaining(deadline));
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(deadline));
    }, 1000);

    return () => clearInterval(interval);
  }, [deadline, breached]);

  if (!deadline && !breached) {
    return null;
  }

  const colors = getColorClasses(timeRemaining, breached, isPaused);
  const StatusIcon = getStatusIcon(timeRemaining, breached, isPaused);
  const timeDisplay = formatTimeDisplay(timeRemaining, isPaused);

  // Badge variant (minimal)
  if (variant === 'badge') {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border ${className}`}>
        {showIcon && <StatusIcon className="w-3 h-3" />}
        <span>{timeDisplay}</span>
      </div>
    );
  }

  // Compact variant (single line)
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${colors.bg} ${colors.border} border ${className}`}>
        {showIcon && <StatusIcon className={`w-4 h-4 ${colors.icon}`} />}
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${colors.text}`}>{label}:</span>
          <span className={`text-sm font-bold ${colors.text}`}>{timeDisplay}</span>
        </div>
        {(breached || timeRemaining.isOverdue) && (
          <div className="ml-auto">
            <span className="text-xs font-semibold text-red-700 uppercase">Breached</span>
          </div>
        )}
      </div>
    );
  }

  // Full variant (detailed)
  return (
    <div className={`${colors.bg} ${colors.border} border-2 rounded-xl p-4 ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {showIcon && (
            <div className={`p-2 rounded-lg ${colors.bg} ${colors.border} border`}>
              <StatusIcon className={`w-5 h-5 ${colors.icon}`} />
            </div>
          )}
          <div>
            <h4 className={`text-sm font-semibold ${colors.text}`}>{label}</h4>
            {deadline && (
              <p className="text-xs text-gray-600">
                Due: {new Date(deadline).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        {(breached || timeRemaining.isOverdue) && (
          <div className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded-full text-xs font-bold">
            <AlertTriangle className="w-3 h-3" />
            <span>BREACHED</span>
          </div>
        )}
      </div>

      {/* Time Display */}
      <div className="flex items-baseline gap-2">
        <div className={`text-3xl font-bold ${colors.text}`}>
          {timeDisplay}
        </div>
        {isPaused && (
          <div className="text-xs text-gray-600 font-medium">
            (Waiting on Customer)
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {deadline && !isPaused && !breached && (
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                timeRemaining.isOverdue
                  ? 'bg-red-500'
                  : timeRemaining.total <= 3600000 // 1 hour
                  ? 'bg-orange-500'
                  : timeRemaining.total <= 14400000 // 4 hours
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{
                width: `${Math.max(0, Math.min(100, (timeRemaining.total / (1000 * 60 * 60 * 24)) * 100))}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Breach Info */}
      {breached && breachedAt && (
        <div className="mt-3 pt-3 border-t border-red-300">
          <p className="text-xs text-red-700 font-medium">
            Breached at: {new Date(breachedAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DUAL SLA TIMER (Response + Resolution)
// ============================================================================

export interface DualSLATimerProps {
  firstResponseDeadline: string | null;
  resolutionDeadline: string | null;
  firstResponseBreached: boolean;
  resolutionBreached: boolean;
  firstResponseBreachedAt?: string | null;
  resolutionBreachedAt?: string | null;
  firstRespondedAt?: string | null;
  isPaused?: boolean;
  variant?: 'compact' | 'full';
  className?: string;
}

export function DualSLATimer({
  firstResponseDeadline,
  resolutionDeadline,
  firstResponseBreached,
  resolutionBreached,
  firstResponseBreachedAt,
  resolutionBreachedAt,
  firstRespondedAt,
  isPaused = false,
  variant = 'full',
  className = ''
}: DualSLATimerProps) {
  const responseComplete = !!firstRespondedAt;

  if (variant === 'compact') {
    return (
      <div className={`flex gap-3 ${className}`}>
        {!responseComplete && (
          <SLATimer
            deadline={firstResponseDeadline}
            breached={firstResponseBreached}
            breachedAt={firstResponseBreachedAt}
            label="First Response"
            variant="compact"
            isPaused={isPaused}
          />
        )}
        <SLATimer
          deadline={resolutionDeadline}
          breached={resolutionBreached}
          breachedAt={resolutionBreachedAt}
          label="Resolution"
          variant="compact"
          isPaused={isPaused}
        />
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
      {!responseComplete && (
        <SLATimer
          deadline={firstResponseDeadline}
          breached={firstResponseBreached}
          breachedAt={firstResponseBreachedAt}
          label="First Response SLA"
          variant="full"
          isPaused={isPaused}
        />
      )}
      <SLATimer
        deadline={resolutionDeadline}
        breached={resolutionBreached}
        breachedAt={resolutionBreachedAt}
        label="Resolution SLA"
        variant="full"
        isPaused={isPaused}
      />
    </div>
  );
}
